<template>
  <div class="closed-end-of-month" :class="{ 'out-of-date': outOfDate }">
    <summary-amount-card
        :previous-month="true"
        :out-of-date="outOfDate"
        :amount="balance"
        :variant="variant"
        :share-button-props="shareButtonProps">
      <template v-slot:title v-if="isMonthBalancePositive">
        <div>מעולה!</div>
        <div><span>{{ monthName }}</span> הסתיים בתזרים חיובי</div>
      </template>
      <template v-slot:title v-else>
        <div><span>{{ monthName }}</span> הסתיים בתזרים שלילי</div>
      </template>
      <template v-slot:footer>
        <div class="total-amounts">
          <div class="total-amounts-row">
            <div>סה"כ הכנסות</div>
            <div class="amount" :class="{ 'positive' : isMonthBalancePositive }">{{ formattedTotalIncome }} ₪ + </div>
          </div>
          <div class="total-amounts-row">
            <div>סה"כ הוצאות</div>
            <div class="amount" :class="{ 'negative' : isMonthBalanceNegative }">{{ formattedTotalExpense }} ₪ - </div>
          </div>
        </div>
        <div v-if="hasSaving">
          <div class="separator"></div>
          <div class="saving-amount-row">
            <div>מתוך ההוצאות, הפקדות לחיסכון </div><div>של {{ savingAmount }} ₪&nbsp;&nbsp;🎉</div>
          </div>
        </div>
        <div class="separator"></div>
        <div class="summary-expense-button" v-on:click="openExpenseSummary()">
          <img :src="require('@/assets/icons/graph-bars.svg')">
          סיכום ההוצאות החודשיות
        </div>
      </template>
    </summary-amount-card>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import SummaryAmountCard from '@/base-components/cashflow/SummaryAmountCard';
import moneyUtils from '@/utils/money';
import cashflowHistory from '@/store/utilities/cashflow-history';
import ClosedEndOfMonthAsset
  from '@/pages/responsive-pages/authenticated/navigation-pages/cashflow-app/cashflow-view/past-editable-month/components/ClosedEndOfMonthAsset';
import ExpenseSummary
  from '@/pages/responsive-pages/authenticated/navigation-pages/cashflow-app/cashflow-view/expense-summary/ExpenseSummary';
import SavingCategory from '@/mixins/SavingCategory';

export default {
  name: 'ClosedEndOfMonth',
  components: {
    SummaryAmountCard,
  },
  props: {
    outOfDate: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [SavingCategory],
  computed: {
    ...mapState('cashflowView', ['presentedBudgetDate']),
    ...mapGetters('cashflowView', ['monthName', 'balance', 'budgetUpdatedAt', 'totalIncome', 'totalExpense']),
    variant() {
      if (this.isMonthBalancePositive) {
        return 'green';
      }
      return 'red';
    },
    formattedTotalIncome() {
      return moneyUtils.formatAmountWithFraction(this.totalIncome, 0);
    },
    formattedTotalExpense() {
      return moneyUtils.formatAmountWithFraction(this.totalExpense, 0);
    },
    isMonthBalancePositive() {
      return this.balance >= 0;
    },
    isMonthBalanceNegative() {
      return !this.isMonthBalancePositive;
    },
    shareComponent() {
      return ClosedEndOfMonthAsset;
    },
    shareComponentProps() {
      return {
        balance: this.balance,
        monthName: cashflowHistory.getFullMonthName(this.presentedBudgetDate),
      };
    },
    shareButtonProps() {
      return {
        shareComponent: this.shareComponent,
        shareComponentProps: this.shareComponentProps,
        shareName: 'ShareEomOneNumber',
        filename: 'month-number',
      };
    },
    hasSaving() {
      return this.anyFixedSaving || this.anyOneTimeSaving;
    },
    savingAmount() {
      return moneyUtils.formatAmountWithFraction(this.allSavingTransactions.upToNowAmount, 0);
    },
  },
  methods: {
    ...mapActions('modalRootStore', ['openModal']),
    openExpenseSummary() {
      this.openModal({
        component: ExpenseSummary,
        props: {
          initialBudgetDate: this.presentedBudgetDate,
        },
      });
    },
  },
};
</script>

<style lang="scss">
  @import '~base-ui/src/scss/riseup-colors';
  @import '~base-ui/src/scss/riseup-vars';
  @import '@/scss/mixins';

  .closed-end-of-month {
    .total-amounts {
      line-height: 23px;
      display: flex;
      flex-direction: column;
      padding: $card_padding;

      .total-amounts-row {
        @include cashflow-h2;
        display: flex;
        justify-content: space-between;

        .amount {
          color: $riseup_black;

          &.positive {
            color: $riseup_green;
            font-weight: bold;
          }

          &.negative {
            color: $riseup_danger_red;
            font-weight: bold;
          }
        }
      }
    }

    &.out-of-date {
      .total-amounts {
        .total-amounts-row {
          color: $riseup_gray_2;
          .amount {
            color: $riseup_gray_2;
            &.positive {
              font-weight: bold;
            }

            &.negative {
              font-weight: bold;
            }
          }
        }
      }
    }

    .saving-amount-row {
      padding: $card_padding;
      line-height: 24px;
    }

    .footer {
      .summary-expense-button {
        padding: $card_padding;
        text-align: center;
        font-size: 20px;
        cursor: pointer;
        img {
          margin-left: 2px;
        }
      }
      .separator {
        height: 1px;
        width: 100%;
        background: $riseup_gray_0;
        margin: 20px 0;
      }
    }
  }
</style>
